<template>
  <form-base>
    <template #form>
      <v-form ref="form" @keyup.enter="onConfirm">
        <v-row>
          <v-col>
            <text-field
              required
              data-testid="warehouse-name-field"
              v-model="fields.name"
              :tooltipText="
                $t(
                  'warehouse.forms.createWarehouseForm.tooltips.nameOfWarehouseLocationVisibleToWarehouseAndCarriers'
                )
              "
              :rules="
                $validator.rules.required(
                  $t('warehouse.forms.createWarehouseForm.labels.warehouseName')
                )
              "
              :label="$t('warehouse.forms.createWarehouseForm.labels.warehouseName')"></text-field>
          </v-col>
          <v-col>
            <text-field
              data-testid="warehouse-store-number-field"
              v-model="fields.facilityNumber"
              :label="$t('warehouse.forms.createWarehouseForm.labels.storeNumber')"
              :tooltipText="
                $t('warehouse.forms.createWarehouseForm.tooltips.warehouseStoreNumber')
              "></text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <phone-number-field
              data-testid="warehouse-phone-number-field"
              v-model="fields.phone"
              margin-top="-7px"
              :validator="$validator"
              :default-country="defaultPhoneCountry"
              :tooltipText="
                $t(
                  'warehouse.forms.createWarehouseForm.tooltips.warehouseContactPhoneNumberForCarriers'
                )
              "
              :label="$t('global.basicTerms.phone')"></phone-number-field>
          </v-col>
          <v-col>
            <text-field
              data-testid="warehouse-email-field"
              type="email"
              v-model="fields.email"
              :tooltipText="
                $t(
                  'warehouse.forms.createWarehouseForm.tooltips.warehouseContactEmailForCarriersItWillBeUsedAsReplyToAddressOfTheAppointmentEmails'
                )
              "
              :rules="$validator.rules.email"
              :label="
                $t('warehouse.forms.createWarehouseForm.labels.warehouseContactEmail')
              "></text-field>
          </v-col>
        </v-row>

        <v-row>
          <v-col class="align-field" cols="12" sm="6" md="4">
            <country-select
              test-id="warehouse-country-select"
              v-model="editingAddress.country"
              limit-options
              :tooltipText="
                $t('warehouse.forms.createWarehouseForm.tooltips.countryOfTheWarehouseLocation')
              "></country-select>
          </v-col>
          <v-col :class="{ 'align-field': manualAddressMode }">
            <address-autocomplete-field
              required
              test-id="warehouse-address-field"
              :rules="
                editingAddress.street
                  ? []
                  : $validator.rules.required(
                      $t('warehouse.forms.createWarehouseForm.labels.streetAddress')
                    )
              "
              ref="searchAddressField"
              v-if="!manualAddressMode"
              v-model="geocodingData"
              clear-on-country-change
              :disabled="!editingAddress.country"
              :initial-value="editingAddress.street"
              :country="editingAddress.country"
              :tooltipText="
                $t(
                  'warehouse.forms.createWarehouseForm.tooltips.addressOfTheWarehouseLocationVisibleToCarriers'
                )
              "
              @filled="fillFullAddress"
              @manual-override="enterManualAddressMode"></address-autocomplete-field>
            <street-field
              v-else
              test-id="warehouse-street-field"
              ref="manualStreetField"
              @search="enterSearchAddressMode"
              required
              v-model="editingAddress.street"
              :rules="
                $validator.rules.required(
                  $t('warehouse.forms.createWarehouseForm.labels.streetAddress')
                )
              " />
          </v-col>
        </v-row>

        <address-display
          v-if="!manualAddressMode && editingAddress.city"
          :zip="editingAddress.zip"
          :city="editingAddress.city"
          :state="editingAddress.state"
          :street="editingAddress.street"
          :timezone="editingAddress.timezone" />

        <v-row v-else-if="manualAddressMode">
          <v-col cols="12" sm="6" md="4" class="pr-0">
            <text-field
              data-testid="warehouse-city-field"
              required
              type="text"
              v-model="editingAddress.city"
              :rules="$validator.rules.required($t('lists.common.headers.city'))"
              :label="$t('lists.common.headers.city')"></text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <state-select
              test-id="warehouse-state-select"
              v-if="editingAddress.country === 'USA' || editingAddress.country === 'CAN'"
              required
              :rules="$validator.rules.required($t('lists.common.headers.state'))"
              :label="$t('lists.common.headers.state')"
              :country="editingAddress.country"
              v-model="editingAddress.state"></state-select>
            <text-field
              v-else
              data-testid="warehouse-state-field"
              required
              type="text"
              v-model="editingAddress.state"
              :rules="$validator.rules.required($t('lists.common.headers.state'))"
              :label="$t('lists.common.headers.state')"></text-field>
          </v-col>
          <v-col cols="12" sm="6" md="2" class="pl-0">
            <text-field
              required
              data-testid="warehouse-zip-field"
              type="text"
              v-model="editingAddress.zip"
              :rules="$validator.rules.required($t('lists.common.headers.zip'))"
              placeholder="85331"
              :label="$t('warehouse.forms.createWarehouseForm.labels.zipPostalCode')"></text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <timezone-select
              data-testid="warehouse-timezone-select"
              v-model="editingAddress.timezone"
              :rules="
                $validator.rules.required($t('warehouse.forms.createWarehouseForm.labels.timezone'))
              "
              :label="$t('warehouse.forms.createWarehouseForm.labels.timezone')"
              required />
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <email-list-field
              test-id="warehouse-email-list-field"
              class="full-width mb-4"
              :read-only="!$rolePermissions.canUpdateWarehouse"
              :label="$t('warehouse.forms.createWarehouseForm.labels.defaultEmailSubscribers')"
              prepend-icon="mdi-email-outline"
              v-model="fields.ccEmails"
              :tooltipText="
                $t(
                  'warehouse.forms.createWarehouseForm.tooltips.defaultEmailSubscribersForAppointmentsAtThisFacility'
                )
              "
              @keyup.enter.stop
              small-chips
              small></email-list-field>
          </v-col>
        </v-row>

        <v-textarea
          rows="2"
          v-model="fields.notes"
          @keyup.stop
          data-testid="warehouse-internal-notes-field">
          <template v-slot:label>
            <div class="with-tooltip">
              {{ $t('forms.elements.inputs.notesField.label') }}
              <help-icon-tooltip iconStyleClass="grey darken-2">
                {{
                  $t(
                    'warehouse.forms.createWarehouseForm.tooltips.notesThatAreInternalToTheWarehouse'
                  )
                }}
              </help-icon-tooltip>
            </div>
          </template>
        </v-textarea>

        <v-row>
          <v-col>
            <h2 class="mt-16 mb-2 text-black">
              {{ $t('warehouse.forms.createWarehouseForm.headers.schedulingPortalAvailability') }}
            </h2>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="disable-label-click">
            <carrier-scheduling-toggle
              class="pl-2 pb-2"
              :entity="$t('global.specialtyTerms.warehouses')"
              @keyup.stop
              :disabled="!$rolePermissions.canCreateWarehouse"
              v-model="fields.allowCarrierScheduling"></carrier-scheduling-toggle>
          </v-col>
        </v-row>
      </v-form>
    </template>
  </form-base>
</template>

<script>
import autocompleteAddressMixin from '@/components/mixins/autocompleteAddressMixin';

/**
 * Create Warehouse Form
 * @displayName Create Warehouse Form
 */
export default {
  name: 'CreateWarehouseForm',
  mixins: [autocompleteAddressMixin],
  data() {
    return {
      editingAddress: {
        country: 'USA'
      },
      fields: {
        allowCarrierScheduling: false,
        ccEmails: []
      }
    };
  },
  watch: {
    isValid: {
      immediate: true,
      handler(newValue) {
        this.$emit('update:valid', newValue);
      }
    }
  },
  computed: {
    isValid() {
      return Boolean(
        this.fields.name &&
          this.editingAddress.country &&
          this.editingAddress.city &&
          this.editingAddress.state &&
          this.editingAddress.zip &&
          this.editingAddress.timezone
      );
    }
  },
  methods: {
    async onConfirm() {
      if (await this.$isFormValid(this.$refs.form)) {
        this.$emit('confirm', { ...this.fields, ...this.editingAddress });
      }
    }
  }
};
</script>

<style scoped>
.align-field {
  padding-top: 7px !important;
}
</style>
